/**
 * RackAutomationStrip · Start-the-Day (v1.3.0 · 2026-08-19).
 *
 * A compact horizontal 4-step strip that mirrors the four automation
 * milestones the operator was promised:
 *   1. Rack Price Email   → Received hh:mm
 *   2. Price Extraction   → 4 products validated
 *   3. Daily Pricing      → Posted automatically hh:mm
 *   4. Driver Notification→ Scheduled/Sent/Held
 *
 * If anything failed, the strip collapses to a single ACTION REQUIRED
 * banner naming the reason. Read-only.
 */
import { useEffect, useState } from "react";
import { CheckCircle, Warning, Envelope, Files, ArrowsClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";


function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-CA", {
      hour: "numeric", minute: "2-digit", timeZone: "America/Toronto",
    }).toLowerCase().replace(" ", "\u00a0");
  } catch { return "—"; }
}


function Pill({ label, ok, warn, sub, icon: Icon, testid }) {
  const tone = warn
    ? "bg-amber-50 text-amber-800 border-amber-200"
    : ok
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <div
      className={`flex-1 min-w-[180px] rounded-lg border px-3 py-2 ${tone}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider">
        {Icon && <Icon size={12} weight="bold" />}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-sm font-semibold tabular-nums">{sub}</div>
    </div>
  );
}


export default function RackAutomationStrip({ dataTestId = "rack-automation-strip" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/rack-ingestion/status");
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load automation status.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (error) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800" data-testid={`${dataTestId}-error`}>
        <Warning size={16} weight="bold" className="inline mr-1.5" />
        {error}
      </div>
    );
  }

  const pn = data?.todays_pricing;
  const effective = pn || data?.currently_effective_pricing;
  const isCarry = !!data?.is_carried_forward;
  const notice = data?.todays_notice;
  const hasEmail = pn && pn.source === "email_ingestion";
  const emailAt  = pn?.email_received_at;
  const posted   = !!pn?.approved_at;
  const sent     = notice?.status === "sent";
  const held     = notice?.status === "held";
  const productCount = (pn?.approved_mapped_products || pn?.products || []).length;

  return (
    <div
      data-testid={dataTestId}
      className="rounded-lg border border-slate-200 bg-white shadow-sm p-3"
    >
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Rack Price Automation</h3>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>
            {data?.automation_enabled ? "Enabled" : "Disabled (preview)"}
          </span>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1 rounded border border-slate-300 px-1.5 py-0.5 hover:bg-slate-50"
            data-testid="rack-automation-refresh"
          >
            <ArrowsClockwise size={12} weight="bold" /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading automation status…</div>
      ) : isCarry && effective ? (
        <div
          data-testid="rack-automation-carried-forward"
          className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
        >
          <CheckCircle size={16} weight="bold" className="inline mr-1.5 text-emerald-600" />
          Saturday's rack price (effective {effective.effective_date}) remains current — no new supplier email expected today.
        </div>
      ) : held ? (
        <div
          data-testid="rack-automation-action-required"
          className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800"
        >
          <Warning size={16} weight="bold" className="inline mr-1.5" />
          <span className="font-semibold">ACTION REQUIRED — {notice?.reason || "Driver notification held"}</span>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="rack-automation-pills">
          <Pill
            label="Rack Price Email"
            icon={Envelope}
            ok={!!emailAt}
            sub={emailAt ? `Received ${fmtTime(emailAt)}` : "Not received"}
            testid="rack-auto-step-email"
          />
          <Pill
            label="Price Extraction"
            icon={Files}
            ok={productCount >= 4}
            warn={hasEmail && productCount < 4}
            sub={productCount ? `${productCount} products validated` : "Awaiting parse"}
            testid="rack-auto-step-parse"
          />
          <Pill
            label="Daily Pricing"
            icon={CheckCircle}
            ok={posted}
            sub={posted
              ? `${pn.approved_by === "SYSTEM:rack_price_automation" ? "Posted automatically" : "Posted"} ${fmtTime(pn.approved_at)}`
              : "Awaiting supplier email"}
            testid="rack-auto-step-pricing"
          />
          <Pill
            label="Driver Notification"
            icon={Envelope}
            ok={sent}
            warn={!sent && posted}
            sub={
              sent
                ? `Sent ${fmtTime(notice.finished_at)}${notice.recipient_count != null ? " · " + notice.recipient_count + " recipient" + (notice.recipient_count === 1 ? "" : "s") : ""}`
                : posted
                  ? "Scheduled for 6:30 a.m."
                  : "Held"
            }
            testid="rack-auto-step-notice"
          />
        </div>
      )}
    </div>
  );
}
